import { useEffect, useRef, useCallback } from 'react';
import { io, Socket } from 'socket.io-client';
import { sessioneDiDipendente } from '../utils/sezioniDipendente';

// Suffisso `/api` tagliato in coda (non la prima occorrenza): il socket vive
// sull'origine, non sotto il prefisso REST.
const SOCKET_BASE = (process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000/api').replace(/\/api\/?$/, '');

// `repId` e l'id dell'operatore: rappresentante o utente admin, secondo l'area.
// `foto` (percorso relativo) arriva solo per i rappresentanti: gli utenti admin
// non ne hanno e restano con le iniziali.
export interface PresenceViewer { repId: string; name: string; foto?: string }
export interface TypingEvent { repId: string; name: string; field: 'reply' | 'note'; isTyping: boolean }

interface UseSupportSocketOpts {
  /** 'admin' = Centro Ticket (utente User): token e tokenType diversi. */
  area: 'associate' | 'representative' | 'admin';
  ticketId?: string;          // se presente: join_ticket + onTicketEvent
  queue?: boolean;            // se true (solo rappresentante): join_queue + onGroupEvent
  onTicketEvent?: (e: { ticketId: string; type: string }) => void;
  onGroupEvent?: (e: { groupId: string; type: string; ticketId?: string }) => void;
  onPresence?: (e: { ticketId: string; viewers: PresenceViewer[] }) => void;
  onTyping?: (e: TypingEvent) => void;
  enabled?: boolean;          // default true
}

// Hook realtime per i ticket di supporto (namespace /support).
// Gli eventi (ticket/group) portano payload minimale → il chiamante rifetcha il REST.
// presence/typing sono usati solo lato operatore (collision detection).
export function useSupportSocket(opts: UseSupportSocketOpts) {
  const { area, ticketId, queue, enabled = true } = opts;
  const ref = useRef<Socket | null>(null);
  const handlers = useRef(opts);
  handlers.current = opts;

  // Emette l'indicatore "sta scrivendo" (solo operatore).
  const emitTyping = useCallback((tid: string, field: 'reply' | 'note', isTyping: boolean) => {
    ref.current?.emit('typing', { ticketId: tid, field, isTyping });
  }, []);

  useEffect(() => {
    if (!enabled) return;
    if (!ticketId && !queue) return;

    // Chiavi storage per area: l'admin usa lo stesso `accessToken` di axiosInstance.
    const tokenKey =
      area === 'associate' ? 'associate_token'
        : area === 'admin' ? 'accessToken'
          : 'representative_token';
    const token = localStorage.getItem(tokenKey);
    if (!token) return;
    // Il namespace accetta solo il token del titolare: il dipendente non apre il socket (la pagina va col REST)
    if (area === 'associate' && sessioneDiDipendente()) return;

    const socket = io(`${SOCKET_BASE}/support`, {
      path: '/socket.io',
      transports: ['websocket', 'polling'],
      // Il namespace /support riconosce l'utente admin come tokenType 'admin'
      // (backend/src/socket.js): l'actor risultante e di tipo 'user'.
      // Forma callback: socket.io la rivaluta a OGNI (ri)connessione, cosi dopo
      // un refresh del token il reconnect usa quello nuovo e non il token
      // catturato alla prima connessione (che nel frattempo e scaduto).
      auth: (cb: (data: Record<string, unknown>) => void) =>
        cb({ token: localStorage.getItem(tokenKey) || token, tokenType: area })
    });
    ref.current = socket;

    socket.on('connect', () => {
      if (ticketId) socket.emit('join_ticket', { ticketId });
      if (queue) socket.emit('join_queue');
    });
    socket.on('ticket:event', (e: { ticketId: string; type: string }) => handlers.current.onTicketEvent?.(e));
    socket.on('group:event', (e: { groupId: string; type: string; ticketId?: string }) => handlers.current.onGroupEvent?.(e));
    socket.on('presence', (e: { ticketId: string; viewers: PresenceViewer[] }) => handlers.current.onPresence?.(e));
    socket.on('typing', (e: TypingEvent) => handlers.current.onTyping?.(e));
    // Nessun toast: il realtime e un di piu (il chiamante rifetcha comunque via
    // REST). Si logga e si lascia lavorare il retry automatico di socket.io.
    socket.on('connect_error', (err: Error) => {
      console.warn('[support socket] connessione fallita:', err?.message || err);
    });
    socket.on('disconnect', (reason: string) => {
      console.warn('[support socket] disconnesso:', reason);
    });

    return () => {
      if (ticketId) socket.emit('leave_ticket', { ticketId });
      socket.disconnect();
      ref.current = null;
    };
  }, [area, ticketId, queue, enabled]);

  return { emitTyping };
}

export default useSupportSocket;
